import { CATALOG, resolveFees, type Plan, type ResolvedFees } from '@charitystack/entitlements';
import {
  calculateSubscriptionFeeAmount,
  calculateSubscriptionPaymentFees as calculateSharedSubscriptionPaymentFees,
  getFeeAmounts,
  getSubscriptionLegacyFeePolicyDetails as getSharedSubscriptionLegacyFeePolicyDetails,
  normalizeBillingPlan,
  shouldGrossUpCoveredFee,
  type CoveredFeePlan,
  type ResolvedFeeRates,
  type ResolveCoveredFeePlan,
  type ResolveFeeRates,
} from '@charitystack/shared';
// @ts-expect-error - The existing JavaScript record helpers have no declaration file.
import { donorCoversFees } from './paymentRecord';

export const STARTER_PLAN = 'STARTER';

export const getMerchantBillingPlan = (
  merchant: Record<string, unknown> | null | undefined,
): string => {
  // Top-level `billingPlan` is the canonical V1-flat exposure of the
  // processing-fee tier (STARTER / CUSTOM). Fall back to the V2 nested
  // canonical location at billingDetails.processingFees.tier.
  //
  // Do NOT read billingDetails.plan — that field holds the subscription slug
  // (STARTER / PLUS), which is unrelated to the processing-fee tier and would
  // normalize incorrectly.
  if (merchant?.billingPlan) {
    return String(merchant.billingPlan);
  }

  const billingDetails = merchant?.billingDetails;
  if (billingDetails && typeof billingDetails === 'object') {
    const processingFees = (billingDetails as { processingFees?: unknown }).processingFees;
    if (processingFees && typeof processingFees === 'object' && 'tier' in processingFees) {
      return String((processingFees as { tier?: unknown }).tier ?? '');
    }
  }

  return '';
};

export const isStarterBillingPlan = (billingPlan: unknown): boolean =>
  normalizeBillingPlan(billingPlan) === STARTER_PLAN;

/**
 * True when CharityStack absorbs the base fee of a gift made outside the dashboard
 * that the donor does not cover (the no-cover fee waiver). Mirrors the backends'
 * starter_no_cover_fee_waiver_eligible: a STARTER fee deal that opted into Covered
 * Commitment, and never a negotiated org (its rate is echoed, it never pays the
 * covered rate that funds the absorption).
 */
export const isStarterNoCoverFeeWaiverEligible = (
  merchant: Record<string, unknown> | null | undefined,
): boolean => {
  if (!isStarterBillingPlan(getMerchantBillingPlan(merchant))) return false;
  const billingDetails = asRecord(merchant?.billingDetails);
  if (!isTrueFlag(billingDetails.coveredCommitment)) return false;
  const override = billingDetails.entitlementOverrides;
  const isNegotiated =
    typeof override === 'string' &&
    (CATALOG.overrideFeeModes as Record<string, string | undefined>)[override] === 'negotiated';
  return !isNegotiated;
};

/**
 * Rule G (PE-2235) — the shared coveredFee rule the checkout and the charge path use:
 * gross up with no platform fee, or a CUSTOM fee-deal plan that is not catalog-priced.
 */
export const shouldGrossUpCoveredDashboardFees = (
  platformPercentFee: unknown,
  platformFixedFee: unknown,
  plan?: CoveredFeePlan | null,
): boolean =>
  shouldGrossUpCoveredFee(
    platformPercentFee,
    platformFixedFee,
    plan?.billingPlan,
    plan?.isCatalogPricedTier,
  );

// ---------------------------------------------------------------------------
// Backend rate resolution (PE-2235 B1/B3/B4)
//
// A preview must show the rate the backend charges or saves, so this mirrors
// resolve_billing_fee_policy in both backends and asks the entitlement engine for
// every catalog number (the shared package may not import the catalog):
//   1. negotiated (entitlementOverrides profile with feeMode negotiated) → the
//      stored deal, echoed by the engine;
//   2. BASE / PLUS / PRO entitlement plan (billingDetails.plan) → the catalog rate
//      for that plan and billingDetails.coveredCommitment;
//   3. STARTER fee deal → the catalog STARTER rate for coveredCommitment;
//   4. anything else → null, so the stored feeAmounts stay in charge.
// The two backends differ in two details, mirrored here:
//   - transactions-backend (new payments, transaction edits) reaches 1–3 only for a
//     record with any split field, a STARTER fee deal, or a retired-plan marker
//     without legacy fees; edit-donor-details-backend (subscription edits)
//     checks the negotiated profile and the paid tier unconditionally.
//   - they read the fee-deal plan in a different order (infer_billing_plan).
// ---------------------------------------------------------------------------

type FeeRecord = Record<string, unknown>;

const PAID_ENTITLEMENT_PLANS = new Set(['BASE', 'PLUS', 'PRO']);
// Plans the engine prices; a negotiated echo validates the plan, so anything else
// (e.g. the pre-B10 literal CUSTOM) is passed as STARTER — the echo ignores it.
const CATALOG_MODELED_PLANS = new Set(['STARTER', 'BASE', 'PLUS', 'PRO']);
const SPLIT_RATE_FIELDS = [
  'processingPercentFee',
  'processingFixedFee',
  'platformPercentFee',
  'platformFixedFee',
] as const;

const asRecord = (value: unknown): FeeRecord =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as FeeRecord) : {};

// normalize_policy_value
const policyValue = (value: unknown): string =>
  String(value ?? '')
    .trim()
    .toUpperCase()
    .replace(/[- ]/g, '_');

// _bool_or_none(...) or False
const isTrueFlag = (value: unknown): boolean =>
  value === true || (typeof value === 'string' && value.trim().toLowerCase() === 'true');

// The backends read a flattened fee record: rate fields on the top level, falling
// back to a nested feeAmounts map.
const feeField = (record: FeeRecord, field: string): unknown => {
  const top = record[field];
  if (top !== undefined && top !== null) return top;
  return asRecord(getFeeAmounts(record))[field];
};

const hasNumber = (value: unknown): boolean =>
  value !== undefined && value !== null && value !== '' && Number.isFinite(Number(value));

const bumpShare = (record: FeeRecord): number | null => {
  const share = feeField(record, 'checkoutBumpCSSharePercent');
  return hasNumber(share) ? Number(share) : null;
};

const bumpShareInfersStarter = (record: FeeRecord): boolean => {
  const share = bumpShare(record);
  return share === 1 || share === 0.2;
};

// transactions-backend infer_billing_plan: billingDetails.plan, else billingPlan,
// any value other than STARTER counting as CUSTOM. Its org read derives billingPlan
// from billingDetails.processingFees.tier first, so a raw record's tier is read last.
const transactionsFeeDealPlan = (record: FeeRecord): string => {
  const billingDetails = asRecord(record.billingDetails);
  const explicit = policyValue(
    billingDetails.plan || record.billingPlan || asRecord(billingDetails.processingFees).tier,
  );
  if (explicit) return explicit === 'STARTER' ? 'STARTER' : 'CUSTOM';
  return bumpShareInfersStarter(record) ? 'STARTER' : 'CUSTOM';
};

// edit-donor-details-backend infer_billing_plan: billingPlan first, then
// billingDetails.plan; PLUS counts as CUSTOM and any other unknown value is skipped.
const subscriptionEditFeeDealPlan = (record: FeeRecord): string => {
  const billingDetails = asRecord(record.billingDetails);
  for (const candidate of [record.billingPlan, billingDetails.plan]) {
    const normalized = policyValue(candidate);
    if (normalized === 'PLUS') return 'CUSTOM';
    if (normalized === 'STARTER' || normalized === 'CUSTOM') return normalized;
  }
  return bumpShareInfersStarter(record) ? 'STARTER' : 'CUSTOM';
};

// has_removed_plan_marker (identical in both backends).
const hasRemovedPlanMarker = (record: FeeRecord): boolean => {
  const billingDetails = asRecord(record.billingDetails);
  const plans = [billingDetails.plan, record.billingPlan].map(policyValue).filter(Boolean);
  if (plans.some((plan) => plan === 'STARTER' || plan === 'CUSTOM')) return false;
  if (plans.length > 0) return true;
  return bumpShare(record) === 0.25;
};

const toResolvedRates = (fees: ResolvedFees): ResolvedFeeRates | null => {
  const rates = {
    processingPercentFee: Number(fees.processingPct),
    platformPercentFee: Number(fees.platformPct),
    // The engine returns fixed fees in integer cents.
    processingFixedFee: (fees.processingFixed ?? 0) / 100,
    platformFixedFee: (fees.platformFixed ?? 0) / 100,
  };
  return Object.values(rates).every(Number.isFinite) ? rates : null;
};

const catalogRates = (plan: string, coveredCommitment: boolean): ResolvedFeeRates | null =>
  toResolvedRates(resolveFees({ plan: plan as Plan, coveredCommitment }));

const negotiatedRates = (record: FeeRecord): ResolvedFeeRates | null => {
  const billingDetails = asRecord(record.billingDetails);
  const storedPlan = String(billingDetails.plan ?? '').trim().toUpperCase();
  const feeAmounts = Object.fromEntries(
    SPLIT_RATE_FIELDS.map((field) => [field, feeField(record, field)]).filter(
      ([, value]) => value !== undefined && value !== null,
    ),
  );
  return toResolvedRates(
    resolveFees({
      plan: (CATALOG_MODELED_PLANS.has(storedPlan) ? storedPlan : 'STARTER') as Plan,
      entitlementOverrides: billingDetails.entitlementOverrides as never,
      feeAmounts,
    }),
  );
};

/**
 * The rates the backend resolves for one fee record (an organization or a
 * subscription's fee snapshot), or null when the backend uses the stored rates.
 * A record the engine rejects (a negotiated org with no stored deal) also returns
 * null: the stored rates are the best preview there is.
 */
const classifyRecord = (record: FeeRecord, forSubscriptionEdit: boolean) => {
  const billingDetails = asRecord(record.billingDetails);
  const entitlementPlan = String(billingDetails.plan ?? '').trim().toUpperCase();
  const override = billingDetails.entitlementOverrides;
  const isNegotiated =
    typeof override === 'string' &&
    (CATALOG.overrideFeeModes as Record<string, string | undefined>)[override] === 'negotiated';
  const feeDealPlan = forSubscriptionEdit
    ? subscriptionEditFeeDealPlan(record)
    : transactionsFeeDealPlan(record);
  // Rule P (PE-2235): both backends enter the split branch when ANY split field is
  // populated (parsed), never only when all four are.
  const hasSplitFields = SPLIT_RATE_FIELDS.some((field) => hasNumber(feeField(record, field)));
  const hasLegacyFees =
    hasNumber(feeField(record, 'percentFee')) || hasNumber(feeField(record, 'fixedFee'));
  const takesSplitBranch =
    hasSplitFields ||
    feeDealPlan === 'STARTER' ||
    (hasRemovedPlanMarker(record) && !hasLegacyFees);
  return {
    entitlementPlan,
    isPaidTier: PAID_ENTITLEMENT_PLANS.has(entitlementPlan),
    isNegotiated,
    feeDealPlan,
    takesSplitBranch,
  };
};

/**
 * The rule-G plan inputs the backend resolves for one fee record: its fee-deal
 * billingPlan and isCatalogPricedTier (a non-negotiated BASE/PLUS/PRO org that the
 * resolver prices from the catalog — transactions-backend only on its split
 * branch, edit-donor-details-backend unconditionally).
 */
export const resolveBackendCoveredFeePlan: ResolveCoveredFeePlan = (record, { forSubscriptionEdit }) => {
  const { isPaidTier, isNegotiated, feeDealPlan, takesSplitBranch } = classifyRecord(
    asRecord(record),
    forSubscriptionEdit,
  );
  const isCatalogPricedTier =
    !isNegotiated && isPaidTier && (forSubscriptionEdit || takesSplitBranch);
  return { billingPlan: feeDealPlan, isCatalogPricedTier };
};

export const resolveBackendFeeRates: ResolveFeeRates = (record, { forSubscriptionEdit }) => {
  const billingDetails = asRecord(record.billingDetails);
  const coveredCommitment = isTrueFlag(billingDetails.coveredCommitment);
  const entitlementPlan = String(billingDetails.plan ?? '').trim().toUpperCase();
  const isPaidTier = PAID_ENTITLEMENT_PLANS.has(entitlementPlan);
  const override = billingDetails.entitlementOverrides;
  const isNegotiated =
    typeof override === 'string' &&
    (CATALOG.overrideFeeModes as Record<string, string | undefined>)[override] === 'negotiated';
  const feeDealPlan = forSubscriptionEdit
    ? subscriptionEditFeeDealPlan(record)
    : transactionsFeeDealPlan(record);

  // Rule P (PE-2235): both backends enter the split branch when ANY split field is
  // populated (parsed), never only when all four are.
  const hasSplitFields = SPLIT_RATE_FIELDS.some((field) => hasNumber(feeField(record, field)));
  const hasLegacyFees =
    hasNumber(feeField(record, 'percentFee')) || hasNumber(feeField(record, 'fixedFee'));
  const takesSplitBranch =
    hasSplitFields ||
    feeDealPlan === 'STARTER' ||
    (hasRemovedPlanMarker(record) && !hasLegacyFees);

  try {
    if (forSubscriptionEdit) {
      if (isNegotiated) return negotiatedRates(record);
      if (!takesSplitBranch && !isPaidTier) return null;
    } else {
      if (!takesSplitBranch) return null;
      if (isNegotiated) return negotiatedRates(record);
    }
    if (isPaidTier) return catalogRates(entitlementPlan, coveredCommitment);
    if (feeDealPlan === 'STARTER') return catalogRates('STARTER', coveredCommitment);
    return null;
  } catch {
    return null;
  }
};

type SubscriptionPaymentFeesOptions = Omit<
  Parameters<typeof calculateSharedSubscriptionPaymentFees>[0],
  'resolveFeeRates' | 'resolveCoveredFeePlan'
>;

/** A subscription-edit or payment preview at the rate the backend resolves. */
export const calculateSubscriptionPaymentFees = (options: SubscriptionPaymentFeesOptions): number =>
  calculateSharedSubscriptionPaymentFees({
    ...options,
    resolveFeeRates: resolveBackendFeeRates,
    resolveCoveredFeePlan: resolveBackendCoveredFeePlan,
  });

/** The legacy-fee comparison, made on the rates the backend resolves. */
export const getSubscriptionLegacyFeePolicyDetails = (
  options: Omit<
    NonNullable<Parameters<typeof getSharedSubscriptionLegacyFeePolicyDetails>[0]>,
    'resolveFeeRates'
  > = {},
) => getSharedSubscriptionLegacyFeePolicyDetails({ ...options, resolveFeeRates: resolveBackendFeeRates });

export const calculateDashboardFeeAmount = ({
  amount,
  fixedFee = 0,
  percentFee = 0,
  coverFees = true,
  platformPercentFee = null,
  platformFixedFee = null,
  plan = null,
}: {
  amount: unknown;
  fixedFee?: unknown;
  percentFee?: unknown;
  coverFees?: boolean;
  platformPercentFee?: unknown;
  platformFixedFee?: unknown;
  plan?: CoveredFeePlan | null;
}): number => {
  return calculateSubscriptionFeeAmount({
    amount,
    fixedFee,
    percentFee,
    coverFees,
    platformPercentFee,
    platformFixedFee,
    billingPlan: plan?.billingPlan,
    isCatalogPricedTier: plan?.isCatalogPricedTier,
  });
};

/**
 * The fee an edit of an existing gift keeps. transactions-backend never re-prices an
 * online gift on edit (the dashboard sends it no amount), and prices a covered
 * offline edit from the operator's `customFee`, which starts at the stored fee. So
 * an edit shows the gift's stored fee, never one computed from today's rates or its
 * linked subscription: those can differ (a risen org rate prices 10.00 on a gift
 * saved at 7.00).
 */
type StoredGift = { fees?: unknown; coverFees?: unknown };

export const storedGiftFee = (gift: StoredGift): number => {
  if (!donorCoversFees(gift.coverFees)) return 0;
  const fee = Number.parseFloat(String(gift.fees ?? ''));
  return Number.isFinite(fee) ? Math.round(fee * 100) / 100 : 0;
};

export const calculateDashboardPaymentFees = ({
  amount,
  merchant,
  paymentMethod,
  paymentProcessor,
  coverFees = true,
  isEditOfExistingGift = false,
  subscription,
  existingGift,
}: {
  amount: unknown;
  merchant: Record<string, unknown> | null | undefined;
  paymentMethod?: string | null;
  paymentProcessor?: string | null;
  coverFees?: boolean;
  /** Editing an existing transaction: the backend never raises its fee. */
  isEditOfExistingGift?: boolean;
  /**
   * The subscription a transaction edit is linked to. The backend re-prices an edit
   * from that subscription's saved fee snapshot (not today's org record) when it has
   * one, so a preview must pass it to show the fee that will be saved.
   */
  subscription?: Record<string, unknown> | null;
  /**
   * The gift being edited. When given, its stored fee is returned and nothing is
   * priced (see storedGiftFee).
   */
  existingGift?: StoredGift | null;
}): number => {
  if (existingGift) return storedGiftFee(existingGift);
  return calculateSubscriptionPaymentFees({
    amount,
    merchant,
    subscription: subscription ?? undefined,
    paymentMethod,
    paymentProcessor,
    coverFees,
    isEditOfExistingGift,
  });
};

